import { MessageActions } from "@design";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { MessageAuthor } from "@/components/chat/MessageAuthor";
import { Text } from "@/components/Text";
import { CitedMarkdown } from "./CitedMarkdown";
import { splitCitations } from "./citations";
import type { ChatSource, NumberedSources } from "./messageSources";
import { SourceTags, useCitedSources } from "./Sources";

export interface AssistantMessageProps {
  /** The element id, for links to the reply. */
  id?: string;
  /** Agent name when an agent answered; defaults to AnthmAI. */
  name?: string | null;
  /** Formatted time, or a status while streaming. */
  meta?: string;
  text: string;
  sources: ChatSource[];
  /** The sources by number, which the text's `[n]` citations and the sources list use; defaults to `sources`. */
  citations?: NumberedSources;
  /** The finished reply's action buttons (Copy, Try again, More), in its row with what it cited. */
  actions?: ReactNode;
  /** What the reply is doing while it streams (dots, current step, runtime). */
  progress?: ReactNode;
  streaming?: boolean;
  /** Translated failure text. */
  error?: string | null;
  stopped?: boolean;
  /** Live status shown in place of the generating indicator (e.g. a capacity wait). */
  notice?: ReactNode;
  /** What the reply is doing or did, above its text (a research run's progress, then its summary). */
  activity?: ReactNode;
  footer?: ReactNode;
}

/**
 * The list under the answer, numbered like the text's `[n]` when it cites any (or splits Cited / Also consulted).
 * An answer without citations lists its sources as before citations existed, with a repeated file merged (MHS-794).
 */
function listedSources(text: string, sources: ChatSource[], citations: NumberedSources): NumberedSources {
  if (citations.some((source) => source?.cited === true)) return citations;
  return splitCitations(text, (n) => citations[n - 1] != null) ? citations : sources;
}

/** One assistant turn: author row, sources, rendered markdown, citations; live while streaming. */
export function AssistantMessage({
  id,
  name,
  meta,
  text,
  sources,
  citations = sources,
  actions,
  progress,
  streaming = false,
  error,
  stopped = false,
  notice,
  activity,
  footer,
}: Readonly<AssistantMessageProps>) {
  const { t } = useTranslation("chat");
  const cited = useCitedSources(listedSources(text, sources, citations));
  const label = t("message.assistantTurn", { name: name ?? t("composer.defaultAssistant") });
  // It stays through a stall or a capacity wait, whose notices sit under it; a research run shows its own progress.
  const showProgress = streaming && !activity && progress;
  // Until the answer starts, the reply is only its status line (Figma "Chat / Generating"); its header comes with it.
  if (showProgress && !text && !notice && !error) {
    return (
      <article aria-label={label} aria-busy className="space-y-3">
        {progress}
        {footer}
      </article>
    );
  }
  return (
    <article id={id} aria-label={label} aria-busy={streaming} className="scroll-mt-4">
      <MessageAuthor kind="assistant" name={name ?? undefined} meta={meta} />
      <div className="mt-3 ml-10 space-y-3">
        {showProgress}
        {activity}
        <SourceTags sources={sources} />
        {text && <CitedMarkdown text={text} sources={citations} streaming={streaming} />}
        {notice}
        {error && (
          <Text tone="error" role="alert">
            {error}
          </Text>
        )}
        {stopped && (
          <Text variant="caption" tone="tertiary">
            {t("message.stopped")}
          </Text>
        )}
        {footer}
        {!streaming && (actions || cited) && (
          // 5px left, so the buttons' glyphs line up with the text (Figma "Action row").
          <div className="-ml-[5px]">
            <MessageActions sources={cited ?? undefined}>{actions}</MessageActions>
          </div>
        )}
      </div>
    </article>
  );
}
